// frontend/src/components/SMSModal.js
import React, { useState } from 'react';
import { X, MessageSquare, Send, Loader, CheckCircle, XCircle, Users } from 'lucide-react';

const SMSModal = ({ 
  isOpen, 
  onClose, 
  onSend,
  customer = null,          // Client unique (si fourni)
  bulkMode = false,         // Mode envoi groupé
  bulkCount = 0,            // Nombre de destinataires (mode bulk)
  riskLevel = null,         // Niveau de risque (pour affichage)
  templates = []            // Templates disponibles
}) => {
  const [message, setMessage] = useState('');
  const [useTemplate, setUseTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  if (!isOpen) return null;

  const defaultTemplates = [
    {
      id: 'risk_alert',
      name: 'Alerte Risque',
      content: `MAD POWER - ALERTE
        Bonjour {name},

        Votre compte necessite une recharge attention.
        Contactez-nous rapidement.

        MAD POWER`
    },
    {
      id: 'payment_reminder',
      name: 'Rappel Paiement',
      content: `MAD POWER - Rappel

        Bonjour {name},

        Rappel de paiement en attente.
        Merci de regulariser.

        MAD POWER`
    },
    {
      id: 'inactive_warning',
      name: 'Inactivité',
      content: `MAD POWER - Inactivite

        Bonjour {name},

        Aucun achat recent detecte.
        Rechargez pour continuer.

        MAD POWER`
    },
    {
      id: 'custom',
      name: 'Message Personnalisé',
      content: ''
    }
  ];

  const availableTemplates = templates.length > 0 ? templates : defaultTemplates;

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = availableTemplates.find(t => t.id === templateId);
    if (template) {
      if (customer && template.content) {
        setMessage(template.content.replace('{name}', customer.full_name || 'Client'));
      } else if (!customer) {
        setMessage(template.content);
      }
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      alert('⚠️ Le message est vide');
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const response = await onSend(message);
      
      setResult({
        success: true,
        message: response.message || 'SMS envoyé avec succès',
        details: response
      });

      // Auto-close après 2s si succès
      setTimeout(() => {
        onClose();
        resetModal();
      }, 2000);

    } catch (error) {
      setResult({
        success: false,
        message: error.response?.data?.detail || 'Erreur lors de l\'envoi',
        error: error
      });
    } finally {
      setSending(false);
    }
  };

  const resetModal = () => {
    setMessage('');
    setUseTemplate(false);
    setSelectedTemplate('');
    setResult(null);
  };

  const handleClose = () => {
    if (!sending) {
      resetModal();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <MessageSquare size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {bulkMode ? 'Envoi SMS Groupé' : 'Envoyer un SMS'}
              </h2>
              {customer && (
                <p className="text-purple-100 text-sm">
                  À : {customer.full_name} ({customer.phone})
                </p>
              )}
              {bulkMode && (
                <p className="text-purple-100 text-sm flex items-center gap-1">
                  <Users size={16} />
                  {bulkCount} destinataire{bulkCount > 1 ? 's' : ''}
                  {riskLevel && ` • Risque: ${riskLevel}`}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={sending}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Templates */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <input
                type="checkbox"
                checked={useTemplate}
                onChange={(e) => {
                  setUseTemplate(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedTemplate('');
                    setMessage('');
                  }
                }}
                className="w-4 h-4 text-purple-600 rounded"
              />
              Utiliser un modèle
            </label>

            {useTemplate && (
              <select
                value={selectedTemplate}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">-- Choisir un modèle --</option>
                {availableTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message {!useTemplate && '*'}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              maxLength={160}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm"
              placeholder={bulkMode 
                ? "Message groupé (utilisez {name} pour personnaliser)"
                : "Écrire votre message..."}
              disabled={sending || (useTemplate && !selectedTemplate)}
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-xs text-gray-500">
                {message.length}/160 caractères
              </p>
              {message.length > 160 && (
                <p className="text-xs text-orange-600 font-medium">
                  ⚠️ {Math.ceil(message.length / 160)} SMS seront envoyés
                </p>
              )}
            </div>
          </div>

          {/* Preview pour mode bulk */}
          {bulkMode && customer && message && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-medium text-blue-800 mb-2">
                📱 Aperçu (exemple pour {customer.full_name})
              </p>
              <div className="bg-white rounded p-2 text-sm font-mono whitespace-pre-wrap">
                {message.replace('{name}', customer.full_name || 'Client')}
              </div>
            </div>
          )}

          {/* Résultat */}
          {result && (
            <div className={`rounded-lg p-4 ${
              result.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {result.success ? (
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                ) : (
                  <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${
                    result.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {result.message}
                  </p>
                  {result.details && bulkMode && (
                    <div className="mt-2 text-sm">
                      <p className="text-gray-700">
                        ✅ Réussis: <strong>{result.details.success_count}</strong>
                      </p>
                      {result.details.failed_count > 0 && (
                        <p className="text-orange-700">
                          ❌ Échoués: <strong>{result.details.failed_count}</strong>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleClose}
              disabled={sending}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium"
            >
              Annuler
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !message.trim()}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium shadow-lg hover:shadow-xl transition-all"
            >
              {sending ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send size={20} />
                  {bulkMode ? `Envoyer à ${bulkCount}` : 'Envoyer'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSModal;